import { useCallback, useEffect, useState } from "react";

import { API_BASE_URL, getApiHeaders } from "../../../../config";
import {
  allowedListingTypesForUnit,
  validateListingSubmission,
} from "../utils/listingHelpers";
import {
  amenitiesToInputStrings,
  formFromListing,
  initialForm,
} from "../utils/listingFormState";
import { buildListingPayload } from "../utils/listingPayload";

/**
 * Form state, field handlers and the shared create/edit submit of the
 * listing form.
 *
 * Verbatim move out of AdminListingsPage.js (L1 extraction). Create and
 * edit still share one state object and one handler that branches only on
 * `editingListingId` after the payload is built. None of the discovery
 * findings is fixed here: the publish checkbox still overwrites what the
 * table toggle wrote (R1), cancel still leaves the form open (R14), and
 * the rooms effect still has no request guard (R13).
 */
export default function useListingForm({
  listings,
  units,
  cities,
  fetchListings,
  editingListingId,
  setEditingListingId,
  setShowForm,
  images,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [createSuccess, setCreateSuccess] = useState("");
  const [createError, setCreateError] = useState("");

  const [listingType, setListingType] = useState("apartment");
  const [form, setForm] = useState(initialForm);
  const [amenitiesDe, setAmenitiesDe] = useState("");
  const [amenitiesEn, setAmenitiesEn] = useState("");

  const [roomsForSelectedUnit, setRoomsForSelectedUnit] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);

  useEffect(() => {
    if (listingType !== "room" || !form.unit_id.trim()) {
      setRoomsForSelectedUnit([]);
      return;
    }
    setRoomsLoading(true);
    fetch(
      `${API_BASE_URL}/api/admin/units/${encodeURIComponent(form.unit_id)}/rooms`,
      { headers: getApiHeaders() }
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) return [];
          throw new Error("Rooms konnten nicht geladen werden.");
        }
        return res.json();
      })
      .then((data) => setRoomsForSelectedUnit(Array.isArray(data) ? data : []))
      .catch(() => setRoomsForSelectedUnit([]))
      .finally(() => setRoomsLoading(false));
  }, [listingType, form.unit_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleUnitChange = (e) => {
    const unitId = e.target.value;
    const unit = units.find((u) => (u.id || u.unitId) === unitId);
    let cityId = form.city_id;
    if (unit && cities.length > 0) {
      const place = (unit.city || "").trim().toLowerCase();
      const match = cities.find(
        (c) =>
          c.name_de?.toLowerCase() === place ||
          c.slug === place ||
          c.name_en?.toLowerCase() === place
      );
      if (match) cityId = match.id;
    }
    setForm((prev) => ({ ...prev, unit_id: unitId, city_id: cityId, room_id: "" }));
  };

  const handleListingTypeChange = (e) => {
    const value = e.target.value;
    setListingType(value);
    if (value === "apartment") setForm((prev) => ({ ...prev, room_id: "" }));
  };

  const handleCityChange = (e) => {
    setForm((prev) => ({ ...prev, city_id: e.target.value }));
  };

  const handleAddressSelect = useCallback(({ address, lat, lng }) => {
    setForm((prev) => ({
      ...prev,
      address,
      latitude: String(lat),
      longitude: String(lng),
    }));
  }, []);

  const resetFormForNewListing = () => {
    images.setCreatedListingId(null);
    images.setUploadedImages([]);
    setEditingListingId(null);
    images.setExistingImages([]);
    setForm(initialForm);
    setListingType("apartment");
    setAmenitiesDe("");
    setAmenitiesEn("");
    setCreateSuccess("");
    setCreateError("");
    fetchListings();
  };

  const handleEditClick = (listing) => {
    setEditingListingId(listing.id);
    images.setCreatedListingId(null);
    images.setUploadedImages([]);
    setCreateError("");
    setCreateSuccess("");

    setListingType(listing.room_id ? "room" : "apartment");
    setForm(formFromListing(listing));

    const amenityInputs = amenitiesToInputStrings(listing.amenities);
    setAmenitiesDe(amenityInputs.de);
    setAmenitiesEn(amenityInputs.en);

    images.setExistingImages(Array.isArray(listing.images) ? listing.images : []);
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingListingId(null);
    images.setExistingImages([]);
    setForm(initialForm);
    setListingType("apartment");
    setAmenitiesDe("");
    setAmenitiesEn("");
    setCreateError("");
    setCreateSuccess("");
  };

  const handleSubmitCreate = (e) => {
    e.preventDefault();
    setCreateError("");
    setCreateSuccess("");
    const validationError = validateListingSubmission({
      form,
      listingType,
      units,
      listings,
      editingListingId,
    });
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setSubmitting(true);

    const body = buildListingPayload({
      form,
      listingType,
      amenitiesDe,
      amenitiesEn,
      editingListingId,
    });

    const isEdit = !!editingListingId;
    const url = isEdit
      ? `${API_BASE_URL}/api/admin/listings/${encodeURIComponent(editingListingId)}`
      : `${API_BASE_URL}/api/admin/listings`;
    const method = isEdit ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json()
            .then((data) => { throw new Error(data.detail || (isEdit ? "Fehler beim Speichern." : "Fehler beim Erstellen.")); })
            .catch(() => { throw new Error(`Fehler ${res.status}`); });
        }
        return res.json();
      })
      .then((data) => {
        if (isEdit) {
          setCreateSuccess("Änderungen gespeichert.");
          images.setExistingImages(Array.isArray(data.images) ? data.images : images.existingImages);
          fetchListings();
        } else {
          setCreateSuccess("Listing wurde erstellt. Jetzt kannst du Bilder hochladen.");
          images.setCreatedListingId(data.id);
          images.setUploadedImages([]);
          fetchListings();
        }
      })
      .catch((err) => {
        setCreateError(err.message || "Fehler.");
      })
      .finally(() => setSubmitting(false));
  };

  const selectedFormUnit = units.find((u) => u.id === form.unit_id);
  const isSelectedUnitColiving = !!(selectedFormUnit && selectedFormUnit.is_coliving);
  const formListingTypeAllowed = allowedListingTypesForUnit(
    selectedFormUnit,
    listings,
    editingListingId
  );

  return {
    form,
    setForm,
    listingType,
    amenitiesDe,
    amenitiesEn,
    setAmenitiesDe,
    setAmenitiesEn,
    roomsForSelectedUnit,
    roomsLoading,
    submitting,
    createSuccess,
    createError,
    setCreateError,
    isSelectedUnitColiving,
    formListingTypeAllowed,
    handleChange,
    handleUnitChange,
    handleListingTypeChange,
    handleCityChange,
    handleAddressSelect,
    resetFormForNewListing,
    handleEditClick,
    handleCancelEdit,
    handleSubmitCreate,
  };
}
